import { Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useToast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { useUserTourWithUserId } from '@/hooks/use-user-tour-with-user-id';
import { loadColumnPrefs } from '@/lib/column-prefs-persistence';
import { clearDealItemsInProgressToast } from '@/lib/deal-items-toast';
import { resolveDealSignal } from '@/lib/deal-signal-resolution';
import {
  isDealEventsSseEnabled,
  registerDealRealtimeTransportProperty,
} from '@/lib/deal-sse-flag';
import {
  DataSourceStatus,
  DealUpdatedDocument,
} from '@/lib/graphql/generated/graphql';
import { useCategoryTreeMap } from '@/lib/hooks/useCategoryTreeMap';
import { useDeal } from '@/lib/hooks/useDeal';
import { useDealEventStream } from '@/lib/hooks/useDealEventStream';
import { useDealItems } from '@/lib/hooks/useDealItems';
import { useGetAllCountryLabels } from '@/lib/hooks/useGetAllCountryLabels';
import { useShopifyCategoryAiFallback } from '@/lib/hooks/useShopifyCategoryAiFallback';
import { DealItemsStreamError } from '@/lib/streaming/stream-deal-items';
import { showDataSourceErrorToast } from '@/lib/utils/datasource-error';
import { setCurrentWsDealId } from '@/lib/ws-analytics';
import NotFoundPage from '@/pages/not-found';
import {
  columnOrderState,
  DEFAULT_COLUMN_ORDER,
} from '@/states/column-order.state';
import { hiddenSortableColumnsState } from '@/states/column-visibility-user.state';
import { dealIdentifierState } from '@/states/deal-identifier.state';
import { dealAtom } from '@/states/deal.state';
import { pauseCouponCsvRefetchState } from '@/states/pause-coupon-csv-refetch.state';
import {
  dealItemsLoadingState,
  dealItemsRefetchState,
  dealItemsSignalState,
} from '@/states/products.state';
import { useApolloClient, useSubscription } from '@apollo/client/react';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { AlertCircle, Loader2 } from 'lucide-react';

import { DealContext } from '../../lib/contexts/deal.context';
import { usePresence } from '../../lib/hooks/usePresence';

const NO_COMPANY_ASSOCIATED_MESSAGE_REGEX =
  /No company associated to deal\s+(\d+)/i;

function getNoCompanyAssociatedHubspotDealId(message?: string) {
  return message?.match(NO_COMPANY_ASSOCIATED_MESSAGE_REGEX)?.[1] ?? null;
}

function getStreamErrorDetail(error: unknown): string | undefined {
  return error instanceof DealItemsStreamError ? error.body : undefined;
}

export default function Layout() {
  const pauseCouponRefetch = useAtomValue(pauseCouponCsvRefetchState);
  const setDealIdentifier = useSetAtom(dealIdentifierState);
  const setColumnOrder = useSetAtom(columnOrderState);
  const setHiddenSortableColumns = useSetAtom(hiddenSortableColumnsState);
  const user = useUser();
  const { t } = useTranslation();
  const { toast } = useToast();
  const apolloClient = useApolloClient();

  const { dealId } = useParams<{ dealId: string }>();

  useEffect(() => {
    setCurrentWsDealId(dealId ?? null);
    return () => setCurrentWsDealId(null);
  }, [dealId]);

  // Segment analytics by the resolved real-time transport (sse / ws) via a
  // Mixpanel super property.
  useEffect(() => {
    registerDealRealtimeTransportProperty();
  }, []);

  usePresence(dealId!);

  const previousDataSourcesStatusRef = useRef<Map<string, DataSourceStatus>>(
    new Map()
  );

  useCategoryTreeMap();

  useGetAllCountryLabels();

  const {
    deal,
    initialLoading: initialLoadingDeal,
    error: errorDeal,
    refetch: refetchDeal,
  } = useDeal(dealId ?? '');

  useDealItems(dealId ?? '');
  useShopifyCategoryAiFallback(dealId ?? '');
  const { initialLoading: initialLoadingDealItems, error: itemsError } =
    useAtomValue(dealItemsLoadingState);

  const previousActiveVersionIdRef = useRef<string | null>(
    deal?.activeVersionId ?? null
  );

  /**
   * Ordonnance les relectures de deal déclenchées par le canal SSE. Deux signaux
   * rapprochés peuvent résoudre dans le désordre ; sans ce compteur, la plus
   * ancienne écraserait le suivi d'état de `resolveDealSignal` avec un instantané
   * périmé, et le diff suivant rejouerait une transition déjà traitée.
   */
  const dealSignalSequenceRef = useRef(0);

  /**
   * Alternative SSE « signal + refetch » aux subscriptions WebSocket
   * `dealUpdated` / `dealItemsUpdatedV3`. Conditionnée par
   * `isDealEventsSseEnabled` : quand elle est active, les subscriptions WS
   * ci-dessous et dans `useDealItems` se désactivent d'elles-mêmes, et ce canal
   * pilote les relectures à leur place.
   */
  useDealEventStream(dealId, {
    onDeal: () => {
      if (pauseCouponRefetch) return;
      const sequence = ++dealSignalSequenceRef.current;
      refetchDeal()
        .then(({ data }) => {
          if (sequence !== dealSignalSequenceRef.current) return;
          if (data?.deal) {
            resolveDealSignal({
              client: apolloClient,
              deal: data.deal,
              t,
              toast,
            });
          }
        })
        .catch((error: unknown) => {
          console.error('[page-layout] deal signal', error);
        });
    },
    onItems: (action) => {
      getDefaultStore()
        .get(dealItemsSignalState)
        ?.fn(action)
        .catch((error: unknown) => {
          console.error('[page-layout] deal items signal', error);
        });
    },
  });

  useSubscription(DealUpdatedDocument, {
    variables: { dealId: dealId ?? '' },
    skip: !dealId || isDealEventsSseEnabled(),
    onData: ({ data: subscriptionData }) => {
      if (pauseCouponRefetch) {
        return;
      }

      const updated = subscriptionData.data?.dealUpdated;
      if (!updated) return;

      const dataSources = updated.dataSources || [];

      // Read the most recent deal data from the Jotai atom to avoid stale closure issues
      const currentDeal = getDefaultStore().get(dealAtom);

      // Check for version change (e.g., after version restoration)
      const newActiveVersionId = updated.activeVersionId;
      const hasVersionChanged =
        previousActiveVersionIdRef.current &&
        previousActiveVersionIdRef.current !== newActiveVersionId;

      previousActiveVersionIdRef.current = newActiveVersionId;

      if (hasVersionChanged) {
        void getDefaultStore().get(dealItemsRefetchState)?.fn();
      }

      if (currentDeal?.dataSources) {
        const isInitialization =
          previousDataSourcesStatusRef.current.size === 0;

        if (isInitialization) {
          currentDeal.dataSources.forEach((ds) => {
            if ('status' in ds && ds.status) {
              previousDataSourcesStatusRef.current.set(ds.id, ds.status);
            }
          });
        }

        dataSources.forEach((ds) => {
          const dsId = ds.id;
          const previousStatus = previousDataSourcesStatusRef.current.get(dsId);
          const hasPreviousStatus = previousStatus !== undefined;
          const currentStatus = 'status' in ds ? ds.status : null;

          const isAnyToError =
            !isInitialization &&
            hasPreviousStatus &&
            previousStatus !== DataSourceStatus.Error &&
            currentStatus === DataSourceStatus.Error;

          if (isAnyToError) {
            clearDealItemsInProgressToast({
              datasourceId: dsId,
              kind: 'datasourceProcessed',
            });
            const errors = 'errors' in ds ? ds.errors : [];
            showDataSourceErrorToast({
              errors,
              t,
              toast,
              titleKey: 'general.error_import',
              defaultMessageKey: 'general.error_import',
            });
          }

          if (currentStatus) {
            previousDataSourcesStatusRef.current.set(dsId, currentStatus);
          }
        });
      }

      if (!pauseCouponRefetch) {
        // Refetch deal metadata
        refetchDeal();

        // Check if any dataSource status changed to Processed (new items imported)
        // This indicates new products were created from CSV/scraping/previous sales
        const previousDataSourceIds = new Set(
          currentDeal?.dataSources?.map((ds) => ds.id) || []
        );
        const previousDataSourceStatuses = new Map(
          currentDeal?.dataSources?.map((ds) => [ds.id, ds.status]) || []
        );

        // Check for new processed dataSources (new imports completed)
        const hasNewProcessedDataSource = dataSources.some(
          (ds) =>
            !previousDataSourceIds.has(ds.id) &&
            ds.status === DataSourceStatus.Processed
        );

        // Check for status changes to Processed (imports that just completed)
        const hasStatusChangedToProcessed = dataSources.some(
          (ds) =>
            previousDataSourceIds.has(ds.id) &&
            previousDataSourceStatuses.get(ds.id) !==
              DataSourceStatus.Processed &&
            ds.status === DataSourceStatus.Processed
        );

        // Refetch dealItems if there's a new processed dataSource or status changed to Processed
        if (hasNewProcessedDataSource || hasStatusChangedToProcessed) {
          void getDefaultStore().get(dealItemsRefetchState)?.fn();
        }
      }
    },
  });

  useUserTourWithUserId({
    dealId: dealId ?? '',
    dealName: deal?.hubspotInfos?.dealName ?? null,
    dealState: deal?.state,
    editionMode: deal?.editionMode,
    page: user?.isInternalUser ? 'choose' : 'brand',
    brandId: deal?.brandId,
  });

  useEffect(() => {
    const prefs = dealId ? loadColumnPrefs(dealId) : null;
    if (prefs) {
      setColumnOrder(prefs.order);
      setHiddenSortableColumns(prefs.hidden);
    } else if (dealId) {
      setColumnOrder([...DEFAULT_COLUMN_ORDER]);
      setHiddenSortableColumns([]);
    }
    setDealIdentifier(dealId ?? '');
  }, [dealId, setDealIdentifier, setColumnOrder, setHiddenSortableColumns]);

  if (initialLoadingDeal || initialLoadingDealItems) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (errorDeal || itemsError) {
    const noCompanyHubspotDealId =
      getNoCompanyAssociatedHubspotDealId(errorDeal?.message) ||
      getNoCompanyAssociatedHubspotDealId(itemsError?.message) ||
      getNoCompanyAssociatedHubspotDealId(getStreamErrorDetail(itemsError));
    const errorMessage = errorDeal?.message || itemsError?.message;

    if (noCompanyHubspotDealId) {
      if (!user?.isInternalUser) {
        return <NotFoundPage />;
      }

      return (
        <div className="flex h-screen flex-col items-center justify-center px-6">
          <div className="flex max-w-xl flex-col items-center rounded-md border border-amber-200 bg-amber-50 p-6 text-center text-amber-950 shadow-sm">
            <AlertCircle className="h-8 w-8 text-amber-600" />
            <h1 className="mt-4 text-2xl font-semibold text-black">
              {t('page.missing_hubspot_company_title')}
            </h1>
            <p className="mt-3 text-base text-amber-900">
              {t('page.missing_hubspot_company_description')}
            </p>
            <p className="mt-3 rounded-md bg-white/70 px-3 py-2 text-sm text-amber-950">
              {t('page.missing_hubspot_company_transaction_id', {
                id: noCompanyHubspotDealId,
              })}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <AlertCircle className="h-6 w-6 text-red-500" />
        <p className="mt-4 text-lg text-black">{errorMessage}</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!deal) return <NotFoundPage />;

  return (
    <DealContext.Provider value={{ deal, refetchDeal }}>
      <Suspense
        fallback={
          <div className="flex h-screen items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </DealContext.Provider>
  );
}
